import { AbstractControl, FormArray, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Custom validator to check for a minimum length after trimming whitespace from the value.
 * This is a "Validator Factory" because it takes a configuration argument (minLength) 
 * and returns the actual validator function.
 * * @param minLength The minimum number of characters required after trimming.
 * @returns A ValidatorFn that performs the validation.
 */
export function trimmedMinLengthValidator(minLength: number): ValidatorFn {
    // The returned function is the actual validator.
    // Angular will call this function with the form control instance.
    return (control: AbstractControl): { [key: string]: any } | null => {
        const value = control.value;

        // First, check if there's a value at all or if it's not a string.
        // If not, we don't need to validate, so we return null (no errors).
        if (!value || typeof value !== 'string') {
            return null;
        }

        // Use trim() to remove whitespace from the beginning and end of the string.
        const trimmedValue = value.trim();

        // Check if the trimmed value's length meets the minimum requirement.
        if (trimmedValue.length < minLength) {
            // If the validation fails, return an error object. 
            // The key 'trimmedMinLength' can be used in your template to show a specific error message.
            // It's good practice to include details about the error.
            return { 'trimmedMinLength': { requiredLength: minLength, actualLength: trimmedValue.length } };
        }

        // If the validation passes, return null.
        return null;
    };
}

export class CustomFileUploadValidators {

    static minFiles(min: number): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!(control instanceof FormArray)) {
                console.warn('minFiles validator should be applied to a FormArray.');
                return null; // Not applied to a FormArray, so no validation
            }

            if (control.controls.length < min) {
                return { minFiles: { requiredMin: min, actual: control.controls.length } };
            }
            return null;
        };
    }

    static maxFiles(max: number): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!(control instanceof FormArray)) {
                console.warn('maxFiles validator should be applied to a FormArray.');
                return null; // Not applied to a FormArray, so no validation
            }

            if (control.controls.length > max) {
                return { maxFiles: { requiredMax: max, actual: control.controls.length } };
            }
            return null;
        };
    }

    // Optional: Validator for individual file size/type (if needed)
    static fileProperties(allowedMimeTypes: string[], maxIndividualFileSize: number = 0): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const file = control.value as File;

            if (!file) {
                return null; // No file selected, let required validator handle it if needed
            }

            // Validate file type
            if (allowedMimeTypes.length > 0 && !allowedMimeTypes.includes(file.type)) {
                return { invalidFileType: { actualType: file.type, allowedTypes: allowedMimeTypes } };
            }

            // Validate individual file size
            if (maxIndividualFileSize > 0 && file.size > maxIndividualFileSize) {
                return {
                    fileSizeExceeds: {
                        max: maxIndividualFileSize,
                        actual: file.size,
                        formattedMax: `${(maxIndividualFileSize / (1024 * 1024)).toFixed(2)}MB`,
                        formattedActual: `${(file.size / (1024 * 1024)).toFixed(2)}MB`
                    }
                };
            }

            return null;
        };
    }

    static totalFileSize(maxTotalBytes: number): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            if (!(control instanceof FormArray)) {
                console.warn('totalFileSize validator should be applied to a FormArray.');
                return null;
            }

            let totalSize = 0;
            for (const fileControl of control.controls) {
                const file = fileControl.value as File;
                if (file instanceof File) { // Ensure it's a valid File object
                    totalSize += file.size;
                }
            }

            if (totalSize > maxTotalBytes) {
                return {
                    totalFileSizeExceeds: {
                        max: maxTotalBytes,
                        actual: totalSize,
                        formattedMax: `${(maxTotalBytes / (1024 * 1024)).toFixed(0)}MB`,
                        formattedActual: `${(totalSize / (1024 * 1024)).toFixed(2)}MB`
                    }
                };
            }
            return null;
        };
    }




}



